// src/app/(dashboard)/meter/components/MeterCard.tsx
import Link from "next/link";
import React from "react";
import type { MeterRow } from "../types";
import { StatusDot } from "./Badge";
import { CopyButton } from "./CopyButton";
import {
  getMeterStatus,
  getRelativeTime,
  formatReadableTime,
} from "../utils/meterStatus";
import { estimatePeaResidentialBill } from "../utils/peaTariff";

interface MeterCardProps {
  meter: MeterRow;
  now: number; // required — always pass from MeterListClient's ticker
}

function fmtKwh(kwh: number | null): string {
  if (kwh === null) return "-";
  return `${kwh.toLocaleString("th-TH", { maximumFractionDigits: 1 })} kWh`;
}

function fmtBaht(baht: number | null): string {
  if (baht === null) return "-";
  return `${baht.toLocaleString("th-TH", { maximumFractionDigits: 2 })} บาท`;
}

export function MeterCard({ meter, now }: MeterCardProps) {
  const status = getMeterStatus(meter.latest_online_at, now);
  const relativeTime = getRelativeTime(meter.latest_online_at, now);
  const updatedTime = formatReadableTime(meter.updatedAt);

  // Same usage-since-last-billing-read logic as the detail page's billing
  // card: live register now (HES) minus the raw snapshot at the last MDMS
  // billing read. A negative result means the register was reset (e.g.
  // meter swap), so the estimate is withheld rather than shown as bogus.
  const usageSinceLastBilling =
    meter.accumulatedKwh !== null && meter.latestBillingKwh !== null
      ? meter.accumulatedKwh - meter.latestBillingKwh
      : null;
  const estimatedCost =
    usageSinceLastBilling !== null && usageSinceLastBilling >= 0
      ? (estimatePeaResidentialBill(usageSinceLastBilling)?.total ?? null)
      : null;

  return (
    <Link
      href={`/meter/${meter.pea_no}`}
      className={[
        "block rounded-2xl border p-4 shadow-sm transition",
        "border-slate-200 bg-white hover:shadow-md hover:ring-1 hover:ring-slate-200",
        "dark:border-slate-800 dark:bg-slate-900 dark:hover:ring-slate-700",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1 text-base font-bold leading-tight text-purple-700 dark:text-purple-300">
          Pea no. : {meter.pea_no}
          <CopyButton value={meter.pea_no} label="คัดลอก PEA NO." />
        </div>
        <div className="shrink-0 text-sm font-semibold text-slate-700 dark:text-slate-200">
          {fmtKwh(meter.accumulatedKwh)}
        </div>
      </div>

      <div className="mt-2 flex items-start justify-between gap-2 text-[13px] text-slate-700 dark:text-slate-200">
        <div className="flex items-center gap-1">
          <b className="text-slate-900 dark:text-slate-50">MAC Address:</b>{" "}
          {meter.mac_address || "-"}
          {meter.mac_address && (
            <CopyButton value={meter.mac_address} label="คัดลอก MAC" />
          )}
        </div>
        <div className="shrink-0 text-sm font-semibold text-purple-700 dark:text-purple-300">
          {fmtBaht(estimatedCost)}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span>
          <b className="text-slate-700 dark:text-slate-300">ข้อมูลล่าสุด</b>{" "}
          <span
            className={
              status === "online"
                ? "font-medium text-emerald-700 dark:text-emerald-300"
                : status === "offline"
                  ? "text-rose-700 dark:text-rose-300"
                  : ""
            }
          >
            {relativeTime}
          </span>
          {" • Updated: "}
          {updatedTime}
        </span>
        <StatusDot status={status} />
      </div>
    </Link>
  );
}
